/**
 * Custom hook for managing filter history
 * Stores and retrieves previous filter configurations from localStorage
 */

import { useState, useEffect, useCallback } from 'react';
import type { OKRFilters } from '../types/okr';

const HISTORY_KEY = 'okr-filter-history';
const MAX_HISTORY_ITEMS = 10;

export interface FilterHistoryItem {
  id: string;
  timestamp: number;
  filters: OKRFilters;
  label?: string; // Auto-generated label describing the filters
}

/**
 * Generate a human-readable label for a filter configuration
 */
const generateFilterLabel = (filters: OKRFilters): string => {
  const parts: string[] = [];

  if (filters.search) {
    parts.push(`Search: "${filters.search}"`);
  }

  if (filters.types && filters.types.length > 0) {
    parts.push(`Types: ${filters.types.join(', ')}`);
  }

  if (filters.statuses && filters.statuses.length > 0) {
    parts.push(`Status: ${filters.statuses.join(', ')}`);
  }

  if (filters.progressMin !== undefined || filters.progressMax !== undefined) {
    const min = filters.progressMin ?? 0;
    const max = filters.progressMax ?? 100;
    parts.push(`Progress: ${min}%-${max}%`);
  }

  if (filters.ownerIds && filters.ownerIds.length > 0) {
    parts.push(`${filters.ownerIds.length} owner(s)`);
  }

  if (filters.departmentIds && filters.departmentIds.length > 0) {
    parts.push(`${filters.departmentIds.length} department(s)`);
  }

  if (filters.startDate || filters.endDate) {
    parts.push('Date range');
  }

  if (parts.length === 0) {
    return 'All OKRs';
  }

  return parts.join(' | ');
};

/**
 * Check if a filter configuration is "significant" enough to save
 * (i.e., has at least one non-default filter applied)
 */
const isSignificantFilter = (filters: OKRFilters): boolean => {
  return !!(
    (filters.search && filters.search.length > 0) ||
    (filters.types && filters.types.length > 0) ||
    (filters.statuses && filters.statuses.length > 0) ||
    (filters.ownerIds && filters.ownerIds.length > 0) ||
    (filters.departmentIds && filters.departmentIds.length > 0) ||
    filters.progressMin !== undefined ||
    filters.progressMax !== undefined ||
    (filters.startDate && filters.startDate.length > 0) ||
    (filters.endDate && filters.endDate.length > 0)
  );
};

/**
 * Check if two filter configurations are similar (to avoid duplicates)
 */
const areFiltersSimilar = (a: OKRFilters, b: OKRFilters): boolean => {
  return (
    a.search === b.search &&
    JSON.stringify(a.types) === JSON.stringify(b.types) &&
    JSON.stringify(a.statuses) === JSON.stringify(b.statuses) &&
    JSON.stringify(a.ownerIds) === JSON.stringify(b.ownerIds) &&
    JSON.stringify(a.departmentIds) === JSON.stringify(b.departmentIds) &&
    a.progressMin === b.progressMin &&
    a.progressMax === b.progressMax &&
    a.startDate === b.startDate &&
    a.endDate === b.endDate
  );
};

export const useFilterHistory = () => {
  const [history, setHistory] = useState<FilterHistoryItem[]>([]);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setHistory(parsed);
      }
    } catch (error) {
      console.error('Failed to load filter history:', error);
    }
  }, []);

  /**
   * Add a filter configuration to history
   */
  const addToHistory = useCallback((filters: OKRFilters) => {
    // Only save significant filters
    if (!isSignificantFilter(filters)) {
      return;
    }

    setHistory((prev) => {
      // Check if this filter already exists (avoid duplicates)
      const isDuplicate = prev.some((item) => areFiltersSimilar(item.filters, filters));
      if (isDuplicate) {
        return prev;
      }

      // Create new history item
      const newItem: FilterHistoryItem = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
        filters,
        label: generateFilterLabel(filters),
      };

      // Add to beginning and limit to MAX_HISTORY_ITEMS
      const updated = [newItem, ...prev].slice(0, MAX_HISTORY_ITEMS);

      // Save to localStorage
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to save filter history:', error);
      }

      return updated;
    });
  }, []);

  /**
   * Remove a specific history item
   */
  const removeFromHistory = useCallback((id: string) => {
    setHistory((prev) => {
      const updated = prev.filter((item) => item.id !== id);

      // Save to localStorage
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to save filter history:', error);
      }

      return updated;
    });
  }, []);

  /**
   * Clear all history
   */
  const clearHistory = useCallback(() => {
    setHistory([]);
    try {
      localStorage.removeItem(HISTORY_KEY);
    } catch (error) {
      console.error('Failed to clear filter history:', error);
    }
  }, []);

  return {
    history,
    addToHistory,
    removeFromHistory,
    clearHistory,
  };
};
